import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { isUnsupportediOSDevice } from '../../common/utils/userAgent';
import Header from '../header/Header';
import BannersContainer from '../banner/BannersContainer';
import StudentsList from '../students-list/StudentsList';
import Menu from '../menu/Menu';
import PopupsContainer from '../../common/popups/PopupsContainer';
import { appGroupType, applicationsType, institutionType } from '../../constants/prop-types';
import { useAppContext } from '../../common/contexts/AppContext';
import { useAppConfigContext } from '../../common/contexts/AppConfigContext';
import { useFeatureToggleContext } from '../../common/contexts/FeatureToggleContext';
import { APPLICATIONS_GROUPS } from '../../constants/applications';

const Dashboard = ({
    institution,
    applications,
    sortedClasses,
    selectClassId,
    setAssessmentType,
    selectedAppGroup,
    selectAppGroup,
    studentId,
    syncTrigger,
    logoutTrigger,
    isDeviceSupported,
    isUnsyncedData,
    isSyncSuccess,
    isBrowserSupported,
}) => {
    // Should not display BM students data if teacher doesn't have
    // Intervention assessments and licensed to Louisiana asset
    const [showBMTabForInterventionUsers, setStudentListStatus] = useState(true);
    const { hideDidels8WithIntervention } = useFeatureToggleContext();
    const [isBurgerMenuVisible, setBurgerMenuVisibility] = useState(false);
    const openMenu = useCallback(() => setBurgerMenuVisibility(true), []);
    const onBurgerMenuClose = useCallback(() => setBurgerMenuVisibility(false), []);
    const { appConfigs, isSecureAssessmentsEnabled } = useAppConfigContext();

    const { staff, classId, selectedAssessmentType } = useAppContext();

    const appClassNames = classNames('dashboard-app', {
        'dashboard-unsupported-device': isUnsupportediOSDevice(),
    });

    useEffect(() => {
        // Always render student list for PM TAB
        if (hideDidels8WithIntervention && isSecureAssessmentsEnabled) {
            const applicationsForRender = APPLICATIONS_GROUPS[selectedAppGroup].filter(value =>
                appConfigs.hasOwnProperty(value),
            );
            setStudentListStatus(applicationsForRender.length > 0);
        } else {
            setStudentListStatus(true);
        }
    }, [
        appConfigs,
        selectedAssessmentType,
        hideDidels8WithIntervention,
        isSecureAssessmentsEnabled,
        selectedAppGroup,
    ]);

    return (
        <div className={appClassNames}>
            <BannersContainer
                isDeviceSupported={isDeviceSupported}
                isUnsyncedData={isUnsyncedData}
                isSyncSuccess={isSyncSuccess}
                isBrowserSupported={isBrowserSupported}
            />
            <Menu
                staff={staff}
                institution={institution}
                isBurgerMenuVisible={isBurgerMenuVisible}
                onBurgerMenuClose={onBurgerMenuClose}
            />
            <div className='dashboard-gradient' />
            <Header
                classes={sortedClasses}
                syncTrigger={syncTrigger}
                selectedAppGroup={selectedAppGroup}
                selectAppGroup={selectAppGroup}
                selectedClassId={classId}
                selectClassId={selectClassId}
                openMenu={openMenu}
                setAssessmentType={setAssessmentType}
                logoutTrigger={logoutTrigger}
            />
            <div className='dashboard-divider' />
            {showBMTabForInterventionUsers && (
                <StudentsList
                    applications={applications}
                    classId={classId}
                    studentIdFromUrl={studentId}
                />
            )}
            <PopupsContainer />
        </div>
    );
};

Dashboard.propTypes = {
    applications: applicationsType.isRequired,
    institution: institutionType.isRequired,
    sortedClasses: PropTypes.array.isRequired,
    selectClassId: PropTypes.func.isRequired,
    setAssessmentType: PropTypes.func.isRequired,
    selectedAppGroup: appGroupType.isRequired,
    selectAppGroup: PropTypes.func.isRequired,
    studentId: PropTypes.string,
    syncTrigger: PropTypes.func,
    logoutTrigger: PropTypes.func,
    isSyncSuccess: PropTypes.bool,
    isDeviceSupported: PropTypes.bool,
    isUnsyncedData: PropTypes.bool,
    isBrowserSupported: PropTypes.bool,
};

Dashboard.defaultProps = {
    isSyncSuccess: false,
    isDeviceSupported: true,
    isUnsyncedData: false,
    isBrowserSupported: true,
    syncTrigger: () => {},
    logoutTrigger: () => {},
    studentId: undefined,
};

export default Dashboard;
